import { CurrentKey, LoanType, RelationshipType } from '@/helpers/const';
import { formatAddress } from '@/helpers/format';
import { includesEnum } from '@/helpers/includes-enum';
import { initMachine, loanApplicationMachine, type LoanAppMachineContext } from '@/helpers/loanAppMachine';
import type {
  Address,
  Applicant,
  ApplicantId,
  AssetId,
  CustomerApplicationProcessStepEnum,
  CustomerApplicationProcessStepStatus,
  LoanApplication,
  PropertyId,
} from '@/types';
import {
  CustomerApplicationProcessStep,
  CustomerApplicationProcessStepNames,
  CustomerApplicationProcessSteps,
  StepStatus,
} from '@/types';
import { useActor } from '@xstate/vue';
import { computed } from 'vue';
import { interpret } from 'xstate';

const appKey = localStorage.getItem(CurrentKey) ?? '';
const savedState = JSON.parse(localStorage.getItem(appKey) as string);
const initialState: LoanAppMachineContext = savedState ? savedState : initMachine();

const service = interpret(loanApplicationMachine.withContext(initialState)).start();

service.onTransition((state) => {
  if (state.value !== 'gotoReady' && state.value !== 'start') {
    const currentStep = state.value as CustomerApplicationProcessStepEnum;
    if (state.context.status[currentStep] === StepStatus.Incomplete) {
      state.context.status[currentStep] = StepStatus.Ready;
    }

    state.context.current = currentStep;
    const loanApp = state.context.loanApp;
    if (loanApp.applicationId && loanApp.userType) {
      const key = `${loanApp.actionType}-${loanApp.applicationId}`;
      const appState = JSON.stringify(state.context);
      localStorage.setItem(CurrentKey, key);
      localStorage.setItem(key, appState);
    }
  }
});

const processSteps = Object.entries(CustomerApplicationProcessSteps).map(([step, info]) => ({
  id: step as CustomerApplicationProcessStepEnum,
  ...info,
}));

export function useLoanAppProcess() {
  const { state, send } = useActor(service);
  const currentStep = computed(() => state.value.context.current);
  const loanApp = computed(() => state.value.context.loanApp);
  const authorisedUser = state.value.context.authorisedUser;

  const secondaryApplicant = computed(() => {
    return loanApp.value.actionType === 'updateExisting'
      ? loanApp.value.applicants.find((a) => a.id === state.value.context.authorisedUser.id && a.type === 'secondary')
      : undefined;
  });

  const isSecondaryMarried = computed(
    () => secondaryApplicant.value?.relationshipToPrimary === RelationshipType.MARRIED_OR_DEFACTO,
  );

  const stepper = computed<CustomerApplicationProcessStepStatus[]>(() => {
    const currentStep = CustomerApplicationProcessStepNames.includes(state.value.context.current)
      ? state.value.context.current
      : CustomerApplicationProcessStepNames[0];
    return processSteps
      .filter(
        (step) =>
          loanApp.value.userType &&
          step.actions.includes(loanApp.value.actionType) &&
          step.id !== CustomerApplicationProcessStep.Finished &&
          ((loanApp.value.endDebt > 0 &&
            !isSecondaryMarried.value &&
            loanApp.value.loanType !== LoanType.SINGLE_SECURITY) ||
            state.value.context.from === 'crm' ||
            !includesEnum(step.id, [
              CustomerApplicationProcessStep.Income,
              CustomerApplicationProcessStep.Expenses,
              CustomerApplicationProcessStep.Assets,
            ]) ||
            // For Secondary declaration
            (loanApp.value.userType === 'Secondary' &&
              !authorisedUser.id &&
              loanApp.value.actionType === 'updateExisting') ||
            (loanApp.value.endDebt <= 0 &&
              !includesEnum(step.id, [
                CustomerApplicationProcessStep.Income,
                CustomerApplicationProcessStep.Expenses,
                CustomerApplicationProcessStep.Assets,
              ])) ||
            (loanApp.value.loanType === LoanType.SINGLE_SECURITY && step.id === CustomerApplicationProcessStep.Assets)),
      )
      .map((step) => {
        const stepStatus = state.value.context.status[step.id];
        const status =
          stepStatus === StepStatus.Incomplete &&
          CustomerApplicationProcessSteps[step.id].order < CustomerApplicationProcessSteps[currentStep].order
            ? StepStatus.Skipped
            : stepStatus;
        return { ...step, status };
      })
      .filter((step) => step.status !== StepStatus.Skipped);
  });

  const loanProperties = computed(() =>
    [...loanApp.value.incomingProperties, ...loanApp.value.outgoingProperties].reduce((obj, item) => {
      obj[item.id] = formatAddress(item.address as Address);
      return obj;
    }, {} as Record<PropertyId | AssetId, string>),
  );
  const loanApplicants = computed<Record<string, Applicant>>(() =>
    loanApp.value.applicants.reduce((obj, item) => {
      obj[item.id as string] = item;
      return obj;
    }, {} as Record<ApplicantId, Applicant>),
  );

  const saveAndGoNext = (updatedData: Partial<LoanApplication> = {}) => {
    if (currentStep.value === CustomerApplicationProcessStep.SubmitApplication) {
      localStorage.setItem(CurrentKey, '');
    }
    send({ type: 'NEXT', loanApp: { ...state.value.context.loanApp, ...updatedData } });
  };

  const goBack = () => {
    send('BACK');
  };

  const restart = (isBroker?: boolean) => {
    send(isBroker === undefined ? 'RESTART' : isBroker ? 'RESTART_BROKER' : 'RESTART_CUSTOMER');
  };

  return {
    state,
    service,
    stepper,
    currentStep, // remove?
    authorisedUser,
    loanApp,
    // targetApplicant: state.value.context.targetApplicant,
    loanProperties,
    loanApplicants,
    send,
    saveAndGoNext,
    goBack,
    restart,
  };
}
